import MaintenanceApiService from "@/service/api/MaintenanceApiService";
import BaseService from "@/service/BaseService";
import {useNotification} from "@/views/core/functions/useNotification";
import {
    GetResultSetWithCountDTO,
    MaintenanceClassName
} from "@/service/api/types/ApiResponseTypes";
import WorkflowGroupAction from "@/domain/maintenance/workflow/WorkflowGroupAction";
import WorkflowEmailConfig, {WorkflowEmailLandingOption, WorkflowEmailPdmFolderOption} from "@/domain/maintenance/workflow/WorkflowEmailConfig";
import WorkflowCondition from "@/domain/maintenance/workflow/WorkflowCondition";
import WfField from "@/domain/maintenance/workflow/WfField";
import WorkflowAction from "@/domain/maintenance/workflow/WorkflowAction";
import WorkflowGARule from "@/domain/maintenance/workflow/WorkflowGARule";
import WorkflowGroup from "@/domain/maintenance/workflow/WorkflowGroup";
import User from "@/domain/profile/User";
import MomentumCustomer from "@/domain/maintenance/MomentumCustomer";
import MomentumProduct from "@/domain/MomentumProduct";
import UserLocation from "@/domain/maintenance/UserLocation";
import Location from "@/domain/maintenance/Location";
import AllMaterial from "@/domain/maintenance/material/AllMaterial";
import MomentumControl from "@/domain/maintenance/ui/MomentumControl";
import ControlRule from "@/domain/maintenance/ui/ControlRule";
import SkillSpecProperty from "@/domain/maintenance/SkillSpecProperty";
import ProposalSkillSpec from "@/domain/maintenance/ProposalSkillSpec";
import ProposalSearchFilter from "@/components/filters/ProposalSearchFilter";
import CopyRule from "@/domain/maintenance/copy/CopyRule";
import CopyType from "@/domain/maintenance/workflow/CopyType";
import WfCopyField from "@/domain/maintenance/workflow/WfCopyField";
import ReasonCodeRule from "@/domain/maintenance/workflow/ReasonCodeRule";
import rootStore from "@/store/RootStore";

export default class MaintenanceService extends BaseService {

    private maintenanceApiService!: MaintenanceApiService;

    constructor() {
        super()
        this.maintenanceApiService = new MaintenanceApiService();
    }

    public async getAllMaterials(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllMaterials();
        if (response.success) {
            this.rootStore.maintenanceStore.materials = response.items? response.items : [];
        }
        return response.success;
    }

    public async getMaterial(materialNumber: string, plantNumber: string): Promise<AllMaterial | undefined> {
        const response = await this.maintenanceApiService.getMaterial(materialNumber, plantNumber);
        return response.item;
    }

    public async getAllContainerModels(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllContainerModels();
        if (response.success) {
            this.rootStore.maintenanceStore.containerModels = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllAirCellRules(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAirCellRules();
        if (response.success) {
            this.rootStore.maintenanceStore.airCellRules = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllAmortCodes(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllAmortCodes();
        if (response.success) {
            this.rootStore.maintenanceStore.amortCodes = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllCustomerPartTypes(): Promise<boolean> {
        const response = await this.maintenanceApiService.getCustomerPartTypes();
        if (response.success) {
            this.rootStore.maintenanceStore.customerPartTypes = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllDesignAttentions(): Promise<boolean> {
        const response = await this.maintenanceApiService.getDesignAttentions();
        if (response.success) {
            this.rootStore.maintenanceStore.designAttentions = response.items? response.items : [];
        }
        return response.success;
    }

    // Deprecated. Being replaced by ProposalStatuses.
    public async getQaStatuses(): Promise<boolean> {
        const response = await this.maintenanceApiService.getQaStatuses();
        if (response.success && response.items) {
            let index = 0;
            for (const item of response.items) {
                this.rootStore.maintenanceStore.qaStatuses.push({ name: item, value: index});
                index++;
            }
            //this.rootStore.maintenanceStore.qaStatuses = response.items;
        }
        return response.success;
    }

    public async getProposalStatuses(): Promise<boolean> {
        const response = await this.maintenanceApiService.getProposalStatuses();
        if(response.success) {
            this.rootStore.maintenanceStore.proposalStatuses = response.items? response.items : [];
        }
        return response.success;
    }

    public async getProductNumbers(): Promise<boolean> {
        const response = await this.maintenanceApiService.getProductNumbers();
        if (response.success) {
            this.rootStore.maintenanceStore.productNumbers = response.items? response.items : [];
        }
        return response.success;
    }

    public async getCustomerPartDescs(): Promise<boolean> {
        const response = await this.maintenanceApiService.getCustomerPartDescs();
        if (response.success) {
            this.rootStore.maintenanceStore.customerPartDescs = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllHeatSealingRules(): Promise<boolean> {
        const response = await this.maintenanceApiService.getHeatSealingRules();
        if (response.success) {
            this.rootStore.maintenanceStore.heatSealingRules = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllHierarchies(): Promise<boolean> {
        const response = await this.maintenanceApiService.getHierarchies();
        if (response.success) {
            this.rootStore.maintenanceStore.hierarchies = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllHoldCodes(): Promise<boolean> {
        const response = await this.maintenanceApiService.getHoldCodes();
        if (response.success) {
            this.rootStore.maintenanceStore.holdCodes = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllMaterialHoldCodes(): Promise<boolean> {
        const response = await this.maintenanceApiService.getMaterialHoldCodes();
        if (response.success) {
            this.rootStore.maintenanceStore.materialHoldCodes = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllMaterialBlanks(): Promise<boolean> {
        const response = await this.maintenanceApiService.getMaterialBlanks();
        if (response.success) {
            this.rootStore.maintenanceStore.materialBlanks = response.items? response.items : [];
        }
        return response.success;
    }

    public async getMaterialUseCodes(): Promise<boolean> {
        const response = await this.maintenanceApiService.getMaterialUseCodes();
        if (response.success) {
            this.rootStore.maintenanceStore.materialUseCodes = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllMfgFactors(): Promise<boolean> {
        const response = await this.maintenanceApiService.getMFGFactor();
        if (response.success) {
            this.rootStore.maintenanceStore.mfgFactors = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllMfgRates(): Promise<boolean> {
        const response = await this.maintenanceApiService.getMFGRates();
        if (response.success) {
            this.rootStore.maintenanceStore.mfgRates = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllPlants(): Promise<boolean> {
        const response = await this.maintenanceApiService.getPlants();
        if (response.success) {
            this.rootStore.maintenanceStore.plants = response.items? response.items : [];
        }
        return response.success
    }

    public async getAllProposalSearchFilters(): Promise<boolean> {
        const response = await this.maintenanceApiService.getProposalSearchFilters();
        if (response.success) {
            this.rootStore.maintenanceStore.proposalSearchFilters = response.items? response.items : [];
        }
        return response.success
    }

    public async getAllProjectDefaults(): Promise<boolean> {
        const response = await this.maintenanceApiService.getProjectDefaults();
        if (response.success) {
            this.rootStore.maintenanceStore.projectDefaults = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllProjectTypeCodes(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllProjectTypeCodes();
        if (response.success) {
            this.rootStore.maintenanceStore.projectTypeCodes = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllReasons(): Promise<boolean> {
        const response = await this.maintenanceApiService.getReasons();
        if (response.success) {
            this.rootStore.maintenanceStore.reasons = response.items? response.items : [];
        }
        return response.success
    }

    public async getAllRecycleCodes(): Promise<boolean> {
        const response = await this.maintenanceApiService.getRecycleCodes();
        if (response.success) {
            this.rootStore.maintenanceStore.recycleCodes = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllWorkCenters(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllWorkCenters();
        if (response.success) {
            this.rootStore.maintenanceStore.workCenters = response.items? response.items : [];
        }
        return response.success;
    }

    public async getActiveWorkCenters(): Promise<boolean> {
        const response = await this.maintenanceApiService.getActiveWorkCenters();
        if (response.success) {
            this.rootStore.maintenanceStore.activeWorkcenters = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllWCOperations(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllWCOperations();
        if (response.success) {
            this.rootStore.maintenanceStore.wcOperations = response.items? response.items : [];
        }
        return response.success;
    }

    public async getActiveWCOperations(): Promise<boolean> {
        const response = await this.maintenanceApiService.getActiveWCOperations();
        if (response.success) {
            this.rootStore.maintenanceStore.wcOperations = response.items? response.items : [];
        }
        return response.success;
    }
    //depreciated - not managed in ui
    public async getAllWCMaint(): Promise<boolean> {
        const response = await this.maintenanceApiService.getWCMaints();
        if (response.success) {
            this.rootStore.maintenanceStore.wcMaints = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllToolingRates(): Promise<boolean> {
        const response = await this.maintenanceApiService.getToolingRates();
        if (response.success) {
            this.rootStore.maintenanceStore.toolingRates = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllRoutingPaths(): Promise<boolean> {
        const response = await this.maintenanceApiService.getRoutingPaths();
        if (response.success) {
            this.rootStore.maintenanceStore.routingPaths = response.items? response.items : [];
        }
        return response.success;
    }

    public createHierarchyTypes() {
        this.rootStore.maintenanceStore.createHierarchyTypes();
    }

    public async getAllCurrencies(): Promise<boolean> {
        const response = await this.maintenanceApiService.getCurrencies();
        if (response.success) {
            this.rootStore.maintenanceStore.currencies = response.items? response.items : [];
        }
        return response.success;
    }

    public async getCustomerNames(): Promise<boolean> {
        const response = await this.maintenanceApiService.getCustomerNames();
        if (response.success) {
            this.rootStore.maintenanceStore.customerNames = response.items? response.items : [];
        }
        return response.success;
    }

    public async getSampleAddresses(): Promise<boolean> {
        const response = await this.maintenanceApiService.getSampleAddresses();
        if (response.success) {
            this.rootStore.maintenanceStore.sampleAddresses = response.items? response.items : [];
        }
        return response.success;
    }

    public async getSampleCities(): Promise<boolean> {
        const response = await this.maintenanceApiService.getSampleCities();
        if (response.success) {
            this.rootStore.maintenanceStore.sampleCities = response.items? response.items : [];
        }
        return response.success;
    }

    public async getSampleStates(): Promise<boolean> {
        const response = await this.maintenanceApiService.getSampleStates();
        if (response.success) {
            this.rootStore.maintenanceStore.sampleStates = response.items? response.items : [];
        }
        return response.success;
    }

    public async getSampleCountries(): Promise<boolean> {
        const response = await this.maintenanceApiService.getSampleCountries();
        if (response.success) {
            this.rootStore.maintenanceStore.sampleCountries = response.items? response.items : [];
        }
        return response.success;
    }

    public async getSampleZips(): Promise<boolean> {
        const response = await this.maintenanceApiService.getSampleZips();
        if (response.success) {
            this.rootStore.maintenanceStore.sampleZips = response.items? response.items : [];
        }
        return response.success;
    }

    public async getSampleAttentionTos(): Promise<boolean> {
        const response = await this.maintenanceApiService.getSampleAttentionTos();
        if (response.success) {
            this.rootStore.maintenanceStore.sampleAttentionTos = response.items? response.items : [];
        }
        return response.success;
    }

    public async getSamplePhones(): Promise<boolean> {
        const response = await this.maintenanceApiService.getSamplePhones();
        if (response.success) {
            this.rootStore.maintenanceStore.samplePhones = response.items? response.items : [];
        }
        return response.success;
    }

    public async getProjectManagers(): Promise<boolean> {
        const response = await this.maintenanceApiService.getProjectManagers();
        if (response.success) {
            this.rootStore.maintenanceStore.projectManagers = response.items? response.items : [];
        }
        return response.success;
    }

    public async getRamSam(): Promise<boolean> {
        const response = await this.maintenanceApiService.getRamSam();
        if (response.success) {
            this.rootStore.maintenanceStore.ramSam = response.items? response.items : [];
        }
        return response.success;
    }

    public async getCrmCsm(): Promise<boolean> {
        const response = await this.maintenanceApiService.getCrmCsm();
        if (response.success) {
            this.rootStore.maintenanceStore.crmCsm = response.items? response.items : [];
        }
        return response.success;
    }

    public async getActiveUsers(): Promise<boolean> {
        const response = await this.maintenanceApiService.getActiveUsers();
        if (response.success) {
            this.rootStore.maintenanceStore.activeUsers = response.items? response.items : [];
        }
        return response.success;
    }

    public async getApprovedBy(): Promise<boolean> {
        const response = await this.maintenanceApiService.getApprovedBy();
        if (response.success) {
            this.rootStore.maintenanceStore.approvedBy = response.items? response.items : [];
        }
        return response.success;
    }

    public async getDesigners(): Promise<boolean> {
        const response = await this.maintenanceApiService.getDesigners();
        if (response.success && response.items) {
            const designers: User[] = [];
            response.items.forEach((userJSON) => {
                designers.push(new User(userJSON));
            })
            this.rootStore.maintenanceStore.designers = designers;
        }
        return response.success;
    }

    /**
     * Only updates skills for the designer in current implementation
     * @param designer
     */
    public async updateDesigner(designer: User): Promise<boolean> {
        const original = this.rootStore.maintenanceStore.designers.find((des) => {
            return des.userID == designer.userID;
        })
        const response = await this.maintenanceApiService.updateDesigner(designer);

        if(response.success) {
            const index = original? this.rootStore.maintenanceStore.designers.indexOf(original) : -1;
            if(index >= 0 && response.item) {
                this.rootStore.maintenanceStore.designers[index] = new User(response.item);
            } else {
                useNotification().showSimple('Error')
                console.log("Couldn't refresh designer after successful update");
            }
        }
        return response.success;

    }

    public async getEstimators(): Promise<boolean> {
        const response = await this.maintenanceApiService.getEstimators();
        if (response.success) {
            this.rootStore.maintenanceStore.estimators = response.items? response.items : [];
        }
        return response.success;
    }

    public async getSampleLocations(): Promise<boolean> {
        const response = await this.maintenanceApiService.getSampleLocations();
        if (response.success) {
            this.rootStore.maintenanceStore.sampleLocations = response.items? response.items : [];
        }
        return response.success;
    }

    public async getSampleCodes(): Promise<boolean> {
        const response = await this.maintenanceApiService.getSampleCodes();
        if (response.success) {
            this.rootStore.maintenanceStore.sampleCodes = response.items? response.items : [];
        }
        return response.success;
    }

    public async getSampleMakers(): Promise<boolean> {
        const response = await this.maintenanceApiService.getSampleMakers();
        if(response.success) {
            this.rootStore.maintenanceStore.sampleMakers = response.items? response.items : []
        }
        return response.success;
    }

    public async  getDunnageAttachments(): Promise<boolean> {
        const response = await this.maintenanceApiService.getDunnageAttachments();
        if (response.success) {
            this.rootStore.maintenanceStore.dunnageAttachments = response.items? response.items : [];
        }
        return response.success;
    }

    public async getCommentTypes(): Promise<boolean> {
        const response = await this.maintenanceApiService.getCommentTypes();
        if (response.success) {
            this.rootStore.maintenanceStore.commentTypes = response.items? response.items : [];
        }
        return response.success;
    }

    public async getSampleStatuses(): Promise<boolean> {
        const response = await this.maintenanceApiService.getSampleStatuses();
        if (response.success) {
            this.rootStore.maintenanceStore.sampleStatuses = response.items? response.items : [];
        }
        return response.success;
    }

    /*
        Typically the services handle all the actions/store changes.  To avoid duplicate code the table services
        handle the individual changes while this layer handles errors
     */

    public async addItem<T>(item: T, className: MaintenanceClassName): Promise<{ success: boolean; item?: T }> {
        const response = await this.maintenanceApiService.insertMaintenanceItem<T>(item, className)
        if (response.success) {
            useNotification().showSimple('Saved')
            return {success: response.success, item: response.item}
        } else {
            useNotification().showSimple('Error');
            return {success: false}
        }
    }

    public async editItem<T>(item: T, className: MaintenanceClassName, rowIdentifier: String): Promise<{ success: boolean; item?: T }> {
        const response = await this.maintenanceApiService.updateMaintenanceItem<T>(item, className, rowIdentifier);
        if (response.success) {
            useNotification().showSimple('Saved')
            return {success: response.success, item: response.item}
        } else {
            useNotification().showSimple('Error');
            return {success: false}
        }
    }

    public async deleteItem<T>(item: T, className: MaintenanceClassName): Promise<{ success: boolean }> {
        const response = await this.maintenanceApiService.deleteMaintenanceItem<T>(item, className)
        if (response.success) {
            useNotification().showSimple('Saved')
            return {success: response.success}
        } else {
            useNotification().showSimple('Error');
            return {success: false}
        }
    }

    public async getAllWorkflowGroups(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllWorkflowGroups();
        if (response.success) {
            this.rootStore.maintenanceStore.workflowGroups = response.items? response.items : [];
        }
        return response.success;
    }

    public async duplicateGroupAction(actionId: number): Promise<boolean> {
        const response = await this.maintenanceApiService.duplicateGroupAction(actionId);
        if (response.success) {
            this.rootStore.maintenanceStore.workflowGroupActions = response.items? response.items : [];
        }
        return response.success;
    }

    public async deleteGroupAction(action: WorkflowGroupAction): Promise<boolean> {
        const response = await this.maintenanceApiService.deleteGroupAction(action);
        if (response.success) {
            this.rootStore.maintenanceStore.workflowGroupActions = response.items? response.items : [];
        }
        return response.success;
    }

    public async deleteWorkflowRule(rule: WorkflowGARule): Promise<boolean> {
        const response = await this.maintenanceApiService.deleteWorkflowRule(rule);
        if (response.success && response.item) {
            this.rootStore.maintenanceStore.workflowGARules = response.item.workflowGARules;
            this.rootStore.maintenanceStore.workflowConditions = response.item.workflowConditions;
        }
        return response.success;
    }

    public async deleteWorkflowCondition(condition: WorkflowCondition): Promise<boolean> {
        const response = await this.maintenanceApiService.deleteWorkflowCondition(condition);
        if (response.success) {
            this.rootStore.maintenanceStore.workflowConditions = response.items? response.items : [];
        }
        return response.success;
    }

    public async deleteWorkflowAction(action: WorkflowAction): Promise<boolean> {
        const response = await this.maintenanceApiService.deleteWorkflowAction(action);
        if (response.success) {
            this.rootStore.maintenanceStore.workflowActions = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllWorkflowActions(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllWorkflowActions();
        if (response.success) {
            this.rootStore.maintenanceStore.workflowActions = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllWorkflowGroupActions(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllWorkflowGroupActions();
        if (response.success) {
            this.rootStore.maintenanceStore.workflowGroupActions = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllWorkflowConditions(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllWorkflowConditions();
        if (response.success) {
            this.rootStore.maintenanceStore.workflowConditions = response.items? response.items : [];
        }
        return response.success;
    }

    public async getWorkflowGARulesByActionId(workflowGroupActionId: number): Promise<boolean> {
        if (workflowGroupActionId) {
            const response = await this.maintenanceApiService.getWorkflowGARulesByActionId(workflowGroupActionId);
            if (response.success) {
                this.rootStore.maintenanceStore.workflowGARules = response.items? response.items : [];
            }
            return response.success;
        }
        return false;
    }

    public async getWorkflowGARules(): Promise<boolean> {
        const response = await this.maintenanceApiService.getWorkflowGARules();
        if (response.success && response.item) {
            this.rootStore.maintenanceStore.workflowGARules = response.item.workflowGARules;
            this.rootStore.maintenanceStore.workflowConditions = response.item.workflowConditions;
        }
        return response.success;
    }

    public async updateWorkflowRuleParentCondition(workflowGARule: WorkflowGARule, cond: WorkflowCondition) {
        if (cond.parentCond) {
            workflowGARule.workflowConditionId = cond.workflowConditionId;
        } else {
            workflowGARule.workflowConditionId = -1;
        }
        await this.saveWorkflowGARules([workflowGARule]);
    }

    public async copyWorkflowRule(workflowRule: WorkflowGARule): Promise<boolean> {
        const response = await this.maintenanceApiService.copyWorkflowActionRule(workflowRule);
        if (response.success && response.item) {
            this.rootStore.maintenanceStore.workflowGARules = response.item?.workflowGARules;
            this.rootStore.maintenanceStore.workflowConditions = response.item?.workflowConditions;
        }
        return response.success;
    }
    //pending review of refactor (not used)
    public async getAllWorkflowActionStatuses(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllWorkflowActionStatuses();
        if (response.success) {
            this.rootStore.maintenanceStore.workflowActionStatuses = response.items? response.items : [];
        }
        return response.success;
    }

    //pending review of refactor (not used)
    public async getAllWfActivityTypes(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllWfActivityTypes();
        if (response.success) {
            this.rootStore.maintenanceStore.workflowActivityTypes = response.items? response.items : [];
        }
        return response.success;
    }

    public async saveWorkflowAction(workflowAction: WorkflowGroupAction): Promise<boolean> {
        const response = await this.maintenanceApiService.saveWorkflowAction(workflowAction);
        if (response.success) {
            useNotification().showSimple('Saved Actions')
            this.rootStore.maintenanceStore.workflowGroupActions = response.items? response.items : [];
        }
        return response.success;
    }

    public async saveWorkflowFields(workflowFields: Array<WfField>): Promise<boolean> {
        const response = await this.maintenanceApiService.saveWorkflowFields(workflowFields);
        if (response.success) {
            useNotification().showSimple('Saved Fields')
            this.rootStore.maintenanceStore.workflowCondFields = response.items? response.items : [];
        }
        return response.success;
    }

    public async saveWorkflowActionConditions(workflowConditions: Array<WorkflowCondition>): Promise<boolean> {
        const response = await this.maintenanceApiService.saveWorkflowConditions(workflowConditions);
        if (response.success && response.items) {
            useNotification().showSimple('Saved');
            this.rootStore.maintenanceStore.workflowConditions = response.items.sort((a,b) => {
                return a.sortOrder > b.sortOrder ? 1: -1;
            });
        }
        return response.success;
    }

    public async getWorkflowFieldData(): Promise<boolean> {
        const response = await this.maintenanceApiService.getWorkflowFieldData();
        if (response.success) {
            if (response.item?.wfFields) {
                this.rootStore.maintenanceStore.workflowCondFields = response.item.wfFields;
            }
            if (response.item?.wfFieldTypes) {
                this.rootStore.maintenanceStore.workflowFieldTypes = response.item.wfFieldTypes;
            }
            if (response.item?.wfOperands) {
                this.rootStore.maintenanceStore.workflowOperands = response.item.wfOperands;
            }
            if (response.item?.wfTables) {
                this.rootStore.maintenanceStore.workflowTables = response.item.wfTables;
            }
        }
        return response.success;
    }

    public async validateWorkflowFieldData(): Promise<boolean> {
        const response = await this.maintenanceApiService.validateWorkflowFieldData();
        if (response.success) {
            await this.getWorkflowFieldData();
        }
        return response.success;
    }

    public async saveWorkflowGARules(workflowGARules: Array<WorkflowGARule>): Promise<boolean> {
        const response = await this.maintenanceApiService.saveWorkflowGARules(workflowGARules);
        if (response.success) {
            useNotification().showSimple('Saved')
            this.rootStore.maintenanceStore.workflowGARules = response.items? response.items : [];
        }
        return response.success;
    }

    public async saveWorkflowActions(actions: Array<WorkflowAction>): Promise<boolean> {
        const response = await this.maintenanceApiService.saveWorkflowActions(actions);
        if (response.success) {
            useNotification().showSimple('Saved')
            this.rootStore.maintenanceStore.workflowActions = response.items? response.items : [];
        }
        return response.success;
    }

    public async saveWorkflowGroups(groups: Array<WorkflowGroup>): Promise<boolean> {
        const response = await this.maintenanceApiService.saveWorkflowGroups(groups);
        if (response.success) {
            useNotification().showSimple('Saved')
            this.rootStore.maintenanceStore.workflowGroups = response.items? response.items : [];
        }
        return response.success;
    }

    public async getPriceHoldList(): Promise<boolean> {
        const response = await this.maintenanceApiService.getPriceHoldList();
        if (response.success) {
            this.rootStore.maintenanceStore.priceHolds = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllSampleCarriers(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllSampleCarriers();
        if (response.success) {
            this.rootStore.maintenanceStore.sampleCarriers = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllSkillGroups(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllSkillGroups();
        if(response.success) {
            this.rootStore.maintenanceStore.skillGroups = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllSkills(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllSkills();
        if (response.success) {
            this.rootStore.maintenanceStore.skills = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllMomentumProducts(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllMomentumProducts();
        if(response.success) {
            this.rootStore.maintenanceStore.momentumProducts = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllMomentumCustomers(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllMomentumCustomers();
        if(response.success && response.items) {
            const customers: MomentumCustomer[] = [];
            response.items.forEach((customerJSON) => {
                customers.push(new MomentumCustomer(customerJSON));
            })
            this.rootStore.maintenanceStore.momentumCustomers = customers;
        }
        return response.success;
    }

    public async addMomentumCustomer(customer: MomentumCustomer): Promise<boolean> {
        const response = await this.maintenanceApiService.addMomentumCustomer(customer);
        if(response.success) {
            this.rootStore.maintenanceStore.momentumCustomers.push(new MomentumCustomer(response.item));
            useNotification().showSimple("Saved");
        }
        return response.success;
    }

    public async addMomentumProductType(productType: MomentumProduct): Promise<boolean> {
        const response = await this.maintenanceApiService.addMomentumProductType(productType);
        if(response.success) {
            this.rootStore.maintenanceStore.momentumProducts.push(response.item as MomentumProduct);
            useNotification().showSimple("Saved");
        }
        return response.success;
    }

    public async removeMomentumCustomer(customerID: number): Promise<boolean> {
        const response = await this.maintenanceApiService.removeMomentumCustomer(customerID);
        if(response.success) {
            this.rootStore.maintenanceStore.momentumCustomers = this.rootStore.maintenanceStore.momentumCustomers.filter((customer) => {
                return customer.id != customerID;
            })
            useNotification().showSimple("Saved")
        }
        return response.success;
    }

    public async removeMomentumProductType(productTypeID: number): Promise<boolean> {
        const response = await this.maintenanceApiService.removeMomentumProductType(productTypeID);
        if(response.success) {
            this.rootStore.maintenanceStore.momentumProducts = this.rootStore.maintenanceStore.momentumProducts.filter((productType) => {
                return productType.id != productTypeID;
            })
            useNotification().showSimple("Saved");
        }
        return response.success;
    }

    public async updateMomentumCustomer(customer: MomentumCustomer) {
        const response = await this.maintenanceApiService.updateMomentumCustomer(customer);
        if(response.success) {
            useNotification().showSimple("Saved");
        }
        return response.success;
    }

    public async getLocations(): Promise<boolean> {
        const response = await this.maintenanceApiService.getLocations();
        if (response.success) {
            this.rootStore.userLocationStore.locations = response.items? response.items : [];
        }
        return response.success;
    }

    public async getUserLocations(): Promise<boolean> {
        const response = await this.maintenanceApiService.getUserLocations();
        if (response.success) {
            this.rootStore.userLocationStore.userLocations = response.items? response.items : [];
        }
        return response.success;
    }

    public async addLocation(locationName: string): Promise<boolean> {
        let location = new Location();
        location.active = true;
        location.description = locationName;
        const response = await this.maintenanceApiService.addLocation(location);
        if (response.success) {
            this.rootStore.userLocationStore.locations = response.items? response.items : [];
            useNotification().showSimple('Saved')
        }
        return response.success;
    }

    public async addUserLocations(userLocations: Array<UserLocation>): Promise<boolean> {
        const response = await this.maintenanceApiService.addUserLocations(userLocations);
        if (response.success) {
            this.rootStore.userLocationStore.userLocations = response.items? response.items : [];
            useNotification().showSimple('Saved')
        }
        return response.success;
    }

    public async deleteLocation(location: Location): Promise<boolean> {
        const response = await this.maintenanceApiService.deleteLocation(location.locationId);
        if (response.success) {
            this.rootStore.userLocationStore.locations = response.items? response.items : [];
            useNotification().showSimple('Saved')
        }
        return response.success;
    }

    public async deleteUserLocation(userLocation: UserLocation): Promise<boolean> {
        if (userLocation.userLocationId) {
            const response = await this.maintenanceApiService.deleteUserLocation(userLocation.userLocationId);
            if (response.success) {
                this.rootStore.userLocationStore.userLocations = response.items? response.items : [];
                useNotification().showSimple('Saved')
            }
            return response.success;
        }
        useNotification().showSimple('Error');
        return false;
    }

    public async getPriceListHeaders(): Promise<boolean> {
        const response = await this.maintenanceApiService.getPriceListHeaders();
        if (response.success) {
            this.rootStore.maintenanceStore.priceListHeaders = response.items? response.items : [];
        }
        return response.success;
    }

    public async getTextTemplatesHeaders(): Promise<boolean> {
        const response = await this.maintenanceApiService.getTextTemplateHeaders();
        if (response.success) {
            this.rootStore.maintenanceStore.textTemplateHeaders = response.items? response.items : [];
        }
        return response.success;
    }

    public async getTextTemplatesItems(): Promise<boolean> {
        const response = await this.maintenanceApiService.getTextTemplateItems();
        if (response.success) {
            this.rootStore.maintenanceStore.textTemplateItems = response.items? response.items : [];
        }
        return response.success;
    }

    public async getEffortCodeOptions(): Promise<boolean> {
        const response = await this.maintenanceApiService.getEffortCodeOptions();
        if (response.success) {
            this.rootStore.maintenanceStore.effortCodeOptions = response.items ? response.items : [];
        }
        return response.success;
    }

    public async getFOBDescriptions(): Promise<boolean> {
        const response = await this.maintenanceApiService.getFOBDescriptions();
        if (response.success) {
            this.rootStore.maintenanceStore.fobDescriptions = response.items? response.items: [];
        }
        return response.success;
    }

    public async getAllControlDetails(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllControlDetails();
        if (response.success) {
            this.rootStore.appConfigStore.controls = response.items? response.items: [];
        }
        return response.success;
    }

    public async getAllControls(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllControls();
        if (response.success) {
            this.rootStore.maintenanceStore.momentumControls = response.items? response.items: [];
        }
        return response.success;
    }

    public async getControlRules(controlName: string, groupId: number): Promise<Array<ControlRule>> {
        const response = await this.maintenanceApiService.getControlRules(controlName, groupId);
        if (response.success) {
            return response.items? response.items: [];
        }
        return [];
    }

    public async deleteControl(control: MomentumControl): Promise<boolean> {
        const response = await this.maintenanceApiService.deleteControl(control);
        return response.success;
    }

    public async deleteControlRule(rule: ControlRule): Promise<boolean> {
        const response = await this.maintenanceApiService.deleteControlRule(rule);
        return response.success;
    }

    public async updateControls(controls: Array<MomentumControl>): Promise<boolean> {
        const response = await this.maintenanceApiService.updateControls(controls);
        if (response.success) {
            await this.getAllControls();
        }
        return response.success;
    }

    public async updateControlConditions(conditions: Array<ControlRule>, controlName: string, groupId: number): Promise<boolean> {
        const response = await this.maintenanceApiService.updateControlConditions(conditions, controlName, groupId);
        return response.success;
    }

    public async getAllSkillsSpecifications(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllSkillsSpecifications();
        if (response.success) {
            this.rootStore.maintenanceStore.skillSpecProperties = response.items? response.items : [];
        }
        return response.success;
    }

    public async getProposalSkillSpecsResult(proposalSearchFilter: ProposalSearchFilter): Promise<GetResultSetWithCountDTO<ProposalSkillSpec>> {
        return await this.maintenanceApiService.getProposalSkillSpecsResult(proposalSearchFilter);
    }

    public async updateSkillSpecProperties(val: Array<SkillSpecProperty>): Promise<Array<SkillSpecProperty>> {
        const response = await this.maintenanceApiService.updateSkillSpecProperties(val);
        return (response && response.items) ? response.items : [];
    }

    public async getAllSpecificationSets(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllSpecificationSets();
        if (response.success) {
            this.rootStore.maintenanceStore.specificationValues = response.items? response.items : [];
        }
        return response.success;
    }

    public async getProductionUsers(): Promise<boolean> {
        const response = await this.maintenanceApiService.getProductionUsers();
        if (response.success) {
            this.rootStore.maintenanceStore.productionUsers = response.items? response.items : [];
        }
        return response.success;
    }

    public async getPdmTransitions(): Promise<boolean> {
        const response = await this.maintenanceApiService.getPdmTransitions();
        if (response.success) {
            this.rootStore.maintenanceStore.pdmTransitions = response.items? response.items : [];
        }
        return response.success;
    }

    public async getCopyRules(): Promise<boolean> {
        const response = await this.maintenanceApiService.getCopyRules();
        if (response.success) {
            this.rootStore.proposalCopyStore.copyRules = response.items? response.items : [];
        }
        return response.success;
    }

    public async getAllCopyRules(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllCopyRules();
        if (response.success) {
            this.rootStore.proposalCopyStore.copyRules = response.items? response.items : [];
        }
        return response.success;
    }

    public async updateCopyRule(copyRule: CopyRule): Promise<boolean> {
        const response = await this.maintenanceApiService.updateCopyRule(copyRule);
        if (response.success) {
            if (response.items) {
                this.rootStore.proposalCopyStore.copyRules = response.items;
            }
            useNotification().showSimple("Saved");
        } else {
            useNotification().showSimple("Error saving rule");
        }
        return response.success;
    }

    public async updateCopyTypes(copyTypes: Array<CopyType>): Promise<boolean> {
        const response = await this.maintenanceApiService.updateCopyTypes(copyTypes);
        if (response.success && response.items) {
            this.rootStore.proposalCopyStore.copyTypes = response.items;
            useNotification().showSimple("Saved");
        } else {
            useNotification().showSimple("Error");
        }

        return response.success;
    }

    public async getCopyTypes(): Promise<boolean> {
        const response = await this.maintenanceApiService.getCopyTypes();
        if (response.success) {
            this.rootStore.proposalCopyStore.copyTypes = response.items ? response.items : [];
        }
        return response.success;
    }

    public async deleteCopyField(copyField: WfCopyField): Promise<boolean> {
        const response = await this.maintenanceApiService.deleteCopyField(copyField);
        return response.success;
    }

    public async deleteCopyType(copyType: CopyType): Promise<boolean> {
        const index = this.rootStore.proposalCopyStore.copyTypes.indexOf(copyType);
        if (copyType.copyTypeId && copyType.copyTypeId > 0) {
            const response = await this.maintenanceApiService.deleteCopyType(copyType);
            if (response.success) {
                this.rootStore.proposalCopyStore.copyTypes.splice(index, 1);
            }
            return response.success;
        } else {
            this.rootStore.proposalCopyStore.copyTypes.splice(index, 1);
            return true;
        }
    }

    public async importCopyRuleDocument(file: File): Promise<boolean> {
        const response = await this.maintenanceApiService.importCopyRuleDocument(file);
        if (response.success) {
            useNotification().showSimple("Records imported");
        } else {
            useNotification().showSimple("Import failed");
        }
        return response.success;
    }

    public async deleteReasonCodeRule(rcRule: ReasonCodeRule): Promise<boolean> {
        const index = this.rootStore.proposalCopyStore.reasonCodeRules.indexOf(rcRule);
        const response = await this.maintenanceApiService.deleteReasonCodeRule(rcRule);
        if (response.success) {
            this.rootStore.proposalCopyStore.reasonCodeRules.splice(index, 1);
            useNotification().showSimple("Success");
        }
        return response.success;
    }

    public async saveReasonCodeRules(rcRules: Array<ReasonCodeRule>): Promise<boolean> {
        const response = await this.maintenanceApiService.saveReasonCodeRules(rcRules);
        if (response.success && response.items) {
            this.rootStore.proposalCopyStore.reasonCodeRules = response.items;
            useNotification().showSimple("Success");
        } else {
            useNotification().showSimple("Error");
        }
        return response.success;
    }

    public async getAllReasonCodeRules(): Promise<boolean> {
        const response = await this.maintenanceApiService.getAllReasonCodeRules();
        if (response.success && response.items) {
            this.rootStore.proposalCopyStore.reasonCodeRules = response.items;
        }
        return response.success;
    }

    public async getSampleCopyFields(): Promise<boolean> {
        const response = await this.maintenanceApiService.getSampleCopyFields();
        if (response.success) {
            this.rootStore.proposalCopyStore.sampleExclusiveFields = response.items ? response.items : [];
        }
        return response.success;
    }

    public async getLoopTypeCodes(): Promise<boolean> {
        const response = await this.maintenanceApiService.getLoopTypeCodes();
        if (response.success) {
            this.rootStore.maintenanceStore.loopTypeCodes = response.items ? response.items : [];
        }
        return response.success;
    }

    public async getRevisionReasons(ptype: string): Promise<boolean> {
        const response = await this.maintenanceApiService.getRevisionReasons(ptype);
        if (response.success && response.items) {
            rootStore.getInstance().proposalCopyStore.revisionReasons = response.items;
        }
        return response.success;
    }

    public async getWorkflowEmailConfig(actionId: number): Promise<WorkflowEmailConfig | null> {
        const response = await this.maintenanceApiService.getWorkflowEmailConfig(actionId);
        return response.success ? response.item ?? null : null;
    }

    public async saveWorkflowEmailConfig(config: WorkflowEmailConfig): Promise<WorkflowEmailConfig | null> {
        const response = await this.maintenanceApiService.saveWorkflowEmailConfig(config);
        return response.success ? response.item ?? null : null;
    }

    public async getWorkflowEmailLandingOptions(): Promise<Array<WorkflowEmailLandingOption>> {
        const response = await this.maintenanceApiService.getWorkflowEmailLandingOptions();
        return response.success && response.items ? response.items : [];
    }

    public async getWorkflowEmailPdmFolderOptions(): Promise<Array<WorkflowEmailPdmFolderOption>> {
        const response = await this.maintenanceApiService.getWorkflowEmailPdmFolderOptions();
        return response.success && response.items ? response.items : [];
    }
}
